import React, { FC, useMemo } from 'react';

import { Center, Text } from '@chakra-ui/react';

import { DataTable } from 'components/modules/DataTable';

import { LocationRow, LocationSku } from './itemsByLocation';
import { buildItemsByLocationColumns } from './itemsByLocationColumns';

type Props = {
  skus: LocationSku[];
  rows: LocationRow[];
  totals: { [skuId: string]: number };
  isLoading: boolean;
};

/** Key for the appended totals row; no location can collide with it. */
const TOTALS_KEY = '__total_items__';

/**
 * The Items by Locations pivot (ClickUp 86bbmcuwa): locations down the side,
 * one column per SKU, and a Total Items row at the end.
 *
 * The totals are appended as an ordinary row because DataTable has no footer
 * slot, and a row reads the same here: the table is scrolled horizontally, so
 * a footer would have to scroll in step with the body anyway.
 */
export const ItemsByLocationTable: FC<Props> = ({
  skus,
  rows,
  totals,
  isLoading,
}) => {
  const columns = useMemo(() => buildItemsByLocationColumns(skus), [skus]);

  // No SKUs means no snapshot for the chosen date, so there is nothing to
  // count against and the locations are not news. Appending the totals row
  // unconditionally would otherwise leave a Location/Wash Hub table standing
  // over empty space, which reads as a broken table rather than no data.
  const hasData = skus.length > 0 && rows.length > 0;

  const data = useMemo<LocationRow[]>(
    () =>
      hasData
        ? [
            ...rows,
            {
              key: TOTALS_KEY,
              location: 'Total Items',
              washHub: '',
              counts: totals,
            },
          ]
        : [],
    [hasData, rows, totals],
  );

  if (!isLoading && !hasData) {
    return (
      <Center p={8}>
        <Text fontSize="20px">No Items available</Text>
      </Center>
    );
  }

  return (
    <DataTable<LocationRow>
      data={data}
      columns={columns}
      isLoading={isLoading}
      fitContent
      getRowId={(row) => row.key}
      renderEmpty={() => (
        <Center p={8}>
          <Text fontSize="20px">No Items available</Text>
        </Center>
      )}
    />
  );
};
